import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LayoutDashboard, Boxes, Wrench, TriangleAlert, ClipboardList, LogOut, Theater, Menu, Users, FileText, Zap } from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/assets", label: "Assets", icon: Boxes },
  { to: "/pat-tests", label: "Tag Tests", icon: Zap },
  { to: "/maintenance", label: "Maintenance", icon: Wrench },
  { to: "/faults", label: "Faults", icon: TriangleAlert },
  { to: "/surveys", label: "Site Surveys", icon: ClipboardList },
  { to: "/reports", label: "Reports", icon: FileText },
];

function NavItems({ items, onNavigate }) {
  return (
    <nav className="flex flex-col gap-1">
      {items.map((n) => {
        const Icon = n.icon;
        return (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={onNavigate}
            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium tap-scale ${
                isActive ? "bg-primary/15 text-primary ring-1 ring-primary/30" : "text-foreground/75 hover:bg-secondary/60 hover:text-foreground"
              }`
            }
          >
            <Icon className="h-[18px] w-[18px]" />
            {n.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const navItems = [...NAV, ...(user?.role === "admin" ? [{ to: "/users", label: "Users", icon: Users }] : [])];

  const Brand = (
    <button onClick={() => navigate("/")} className="flex items-center gap-2.5 px-1 text-left tap-scale">
      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Theater className="h-5 w-5" /></span>
      <div className="leading-tight">
        <div className="font-mono text-sm font-bold tracking-tight text-foreground">Plantagenet</div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Tech Management</div>
      </div>
    </button>
  );

  const UserBox = (
    <div className="mt-auto border-t border-border pt-4">
      <div className="mb-2 px-1">
        <div className="truncate text-xs font-medium text-foreground">{user?.name || user?.email}</div>
        <div className="font-mono text-[10px] uppercase tracking-wider text-primary">{user?.role}</div>
      </div>
      <Button data-testid="logout-btn" onClick={logout} variant="outline" className="w-full justify-start gap-2 tap-scale">
        <LogOut className="h-4 w-4" /> Sign out
      </Button>
    </div>
  );

  return (
    <div className="relative z-10 min-h-screen">
      {/* Mobile top bar */}
      <div className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-border bg-background/85 px-3 backdrop-blur-xl lg:hidden">
        {Brand}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" data-testid="open-menu"><Menu className="h-5 w-5" /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="flex w-72 flex-col p-4">
            <div className="mb-6">{Brand}</div>
            <NavItems items={navItems} onNavigate={() => setOpen(false)} />
            {UserBox}
          </SheetContent>
        </Sheet>
      </div>

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-border p-4 lg:flex">
          <div className="mb-8">{Brand}</div>
          <NavItems items={navItems} />
          {UserBox}
        </aside>

        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
